/**
 * Module-level state and pure helper functions for DrivingMode.
 * Kept at module scope so cleanupDrivingMode() can be called externally
 * (e.g. on session expiry or widget unmount) without a component ref.
 */
import { store } from '../../../state/store';
import { getSttService } from '../../../services/stt';
import { getTtsService } from '../../../services/tts';
import sendAskSoundUrl from '../../../assets/file-sounds-1145-when.ogg';
import endSoundUrl from '../../../assets/end.ogg';
import { applyAudioOutput } from '../../../services/audioOutput';
import { webchatApi } from '../../../services/api';
import { normalizeSpokenDigitSequences } from '../../../utils/spokenDigits';
import { resolveWidgetLocale } from '../../../utils/locale';
import {
  ensureOrchestratorRef,
  getOrchestratorRef,
} from '../../messageList/MessageList.helpers';
import { MutableRefObject } from 'react';

// ---------------------------------------------------------------------------
// Module-level state
// ---------------------------------------------------------------------------
export let lastSpokenMsgId: string | null = null;
export let activeStreamingMsgId: string | null = null;
export let streamedResponseText = '';
export let manualStopRequested = false;
export let sendAfterStopRequested = false;
export let accumulatedFinal = '';
export let lastInterimText = '';

// Track message IDs where TTS failed so we don't loop-retry on 429 / errors
export const failedTtsMsgIds = new Set<string>();
// Track message IDs the user manually stopped so we don't replay them immediately
export const suppressedTtsMsgIds = new Set<string>();

// ---------------------------------------------------------------------------
// Duplicate-send guard - prevents Whisper hallucinations repeating the last phrase
// ---------------------------------------------------------------------------
const SENT_WINDOW_MS = 20_000; // ignore identical transcripts within 20 s

interface SentEntry { text: string; at: number; }
const recentlySent: SentEntry[] = [];

/** Returns true if this exact text was already sent within the duplicate window. */
export function isDuplicateTranscript(text: string): boolean {
  const normalized = text.trim().toLowerCase();
  const cutoff = Date.now() - SENT_WINDOW_MS;
  // Prune old entries
  while (recentlySent.length > 0 && recentlySent[0].at < cutoff) recentlySent.shift();
  return recentlySent.some(e => e.text === normalized);
}

/** Record a transcript as sent so future duplicates can be detected. */
export function recordSentTranscript(text: string): void {
  recentlySent.push({ text: text.trim().toLowerCase(), at: Date.now() });
  // Keep the list bounded
  if (recentlySent.length > 20) recentlySent.shift();
}

let sendAskAudio: HTMLAudioElement | null = null;
let endAudio: HTMLAudioElement | null = null;
let silenceTimer: ReturnType<typeof setTimeout> | null = null;
let noSpeechTimer: ReturnType<typeof setTimeout> | null = null;

// ---------------------------------------------------------------------------
// Setters (used by the component to mutate module-level primitives)
// ---------------------------------------------------------------------------
export function setLastSpokenMsgId(id: string | null): void { lastSpokenMsgId = id; }
export function setActiveStreamingMsgId(id: string | null): void { activeStreamingMsgId = id; }
export function setStreamedResponseText(text: string): void { streamedResponseText = text; }
export function setManualStopRequested(val: boolean): void { manualStopRequested = val; }
export function setSendAfterStopRequested(val: boolean): void { sendAfterStopRequested = val; }
export function setAccumulatedFinal(text: string): void { accumulatedFinal = text; }
export function setLastInterimText(text: string): void { lastInterimText = text; }
export function appendAccumulatedFinal(text: string): void {
  accumulatedFinal += (accumulatedFinal ? ' ' : '') + text;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
export const SILENCE_TIMEOUT_MS = 1100;
export const NO_SPEECH_TIMEOUT_MS = 5000;
/**
 * How many consecutive onNoise callbacks (each = NOISE_STOP_THRESHOLD noise Whisper
 * results) are allowed before ending the session. 3 ≈ 12 noise-only Whisper calls.
 */

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/**
 * Detects whether the user's transcript is asking to stop/exit driving mode.
 * Matches many natural phrasings in English and Hebrew - not just the first word.
 */
export function isStopCommand(text: string): boolean {
  const normalized = text.trim().toLowerCase().replace(/^[.,!?؟\-]+|[.,!?؟\-]+$/g, '').trim();

  // Single-word or short exact matches
  const exactMatches = new Set([
    // English
    'stop', 'cancel', 'halt', 'quiet', 'silence', 'abort',
    'exit', 'quit', 'bye', 'goodbye', 'good bye', 'farewell', 'ciao',
    'done', 'finish', 'finished', 'close', 'end', 'enough', 'leave',
    'thanks', 'thank you',
    "i'm done", 'im done', "that's all", 'thats all', "that's enough",
    'thats enough', 'all done', 'no more', 'over', 'out',
    // Hebrew
    'עצור', 'עצרי', 'תפסיק', 'תפסיקי', 'בטל', 'בטלי', 'שתוק', 'שתקי', 'שתקו',
    'הפסק', 'הפסיקי', 'יציאה', 'צא', 'צאי', 'סגור', 'סגרי', 'סיים', 'סיימי',
    'די', 'מספיק', 'ביי', 'להתראות', 'שלום', 'תודה שלום', 'זה הכל', 'נגמר',
    'הסתיים', 'תודה', 'חתוך', 'שקט', 'תשתוק', 'תשתקי', 'תשתקו',
  ]);
  if (exactMatches.has(normalized)) return true;

  // Short-phrase anywhere-match: if the utterance is brief (≤ 5 words) and
  // contains any strong goodbye/stop signal word, treat it as a stop command.
  // Catches things like "טוב ביי", "אוקי ביי", "יאללה ביי", "thanks bye", "ok goodbye", etc.
  const words = normalized.split(/\s+/);
  if (words.length <= 5) {
    const stopSignals = new Set([
      // English
      'bye', 'goodbye', 'farewell', 'stop', 'done', 'finished', 'enough', 'exit', 'quit', 'end', 'thanks',
      // Hebrew
      'ביי', 'להתראות', 'עצור', 'עצרי', 'תפסיק', 'תפסיקי', 'הפסק', 'הפסיקי',
      'סיים', 'סיימי', 'נגמר', 'הסתיים', 'די', 'מספיק',
    ]);
    if (words.some(w => stopSignals.has(w))) return true;
  }

  // Multi-word phrase patterns
  const phrasePatterns: RegExp[] = [
    // English - exit/stop intent
    /\b(exit|close|end|stop|leave|quit|shut down|turn off)\b.*(driving|chat|session|conversation|this|mode)/,
    /\b(stop|end|close|exit|leave|quit)\s+(the\s+)?(driving|chat|session|conversation|mode)\b/,
    /^(stop|exit|close|quit|end|leave)\b/,
    /turn\s+(this|it)\s+(off|down)/,
    /shut\s+(it|this)\s*(down|off)/,
    /i('m|\s+am)\s+done\b/,
    /that('s|\s+is)\s+(all|enough|it)\b/,
    /i\s+want\s+to\s+(stop|exit|leave|quit|end|go)/,
    /please\s+(stop|exit|leave|quit|end|close)/,
    /(ok|okay)\s+stop\b/,
    /^(?:ok|okay)?[\s,.-]*(?:thanks|thank\s+you)[\s,.!?-]*$/,
    /no\s+(more|thanks|thank\s+you)/,
    /bye[\s-]?bye/,
    /good[\s-]?bye/,
    /see\s+you\b/,
    /talk\s+(to\s+you\s+)?(later|soon)\b/,
    /have\s+a\s+(good|great|nice)\b/,
    /that\s+(will\s+be|is)\s+all\b/,
    /\bwe('re|\s+are)\s+done\b/,
    /\bwe('re|\s+are)\s+finished\b/,
    /disconnect(\s+me)?\b/,
    /log\s*(off|out)\b/,
    /sign\s*(off|out)\b/,
    // Hebrew - stop/session intent
    /לא\s+(רוצה|צריך|צריכה)\s+(יותר|כלום)/,
    /תודה\s+(רבה\s+)?(ולשלום|שלום|ביי)/,
    /אני\s+(סיימתי|גמרתי|רוצה\s+לצאת|רוצה\s+לעצור)/,
    /^(צא|צאי|סגור|סגרי|עצור|עצרי|הפסק|הפסיקי|בטל|בטלי)\b/,
    /מצב\s+נהיגה.*(סגור|עצור|סיים|כבה|יצא)/,
    /(כבה|כבי)\s+(את\s+)?(הצ'אט|המצב|הנהיגה)/,
    /להתראות/,
    // "let's stop/end/pause" - Hebrew: "בוא נעצור", "בואי נסיים", "בוא נפסיק"
    /בוא[יי]?\s+נ(עצור|סיים|פסיק|פסיקה|גמור|פרוש)/,
    /נעצור\s+(את\s+)?(השיחה|הצ'אט|המצב|זה)/,
    /נסיים\s+(את\s+)?(השיחה|הצ'אט|המצב|זה)/,
    /נפסיק\s+(את\s+)?(השיחה|הצ'אט|המצב|זה)/,
    /^(?:\u05d0\u05d5\u05e7\u05d9\u05d9?|\u05d8\u05d5\u05d1)?[\s,.-]*\u05ea\u05d5\u05d3\u05d4(?:\s+\u05e8\u05d1\u05d4)?[\s,.!?-]*$/u,
    // English: "let's stop", "let's end", "let's wrap up"
    /let'?s\s+(stop|end|quit|wrap\s+up|finish|close|pause)/,
    /can\s+we\s+(stop|end|finish|wrap\s+up|close)/,
    /i\s+(think\s+)?(we'?re|am)\s+(done|finished|good)/,
    /wrap\s+it\s+up/,
    /pause\s+(the\s+)?(chat|session|conversation)/,
  ];

  return phrasePatterns.some(pattern => pattern.test(normalized));
}

export function getBestTranscript(): string {
  const text = (accumulatedFinal + (lastInterimText ? ' ' + lastInterimText : '')).trim();
  return text || lastInterimText.trim();
}

export function clearSilenceTimer(): void {
  if (silenceTimer) {
    clearTimeout(silenceTimer);
    silenceTimer = null;
  }
}

export function setSilenceTimer(timer: ReturnType<typeof setTimeout>): void {
  silenceTimer = timer;
}

export function clearNoSpeechTimer(): void {
  if (noSpeechTimer) {
    clearTimeout(noSpeechTimer);
    noSpeechTimer = null;
  }
}

export function setNoSpeechTimer(timer: ReturnType<typeof setTimeout>): void {
  noSpeechTimer = timer;
}

export async function playSendAskSound(): Promise<void> {
  if (typeof Audio === 'undefined') return;
  if (!store.getState().drivingMode.drivingCueSoundsEnabled) return;
  try {
    if (!sendAskAudio) {
      sendAskAudio = new Audio(sendAskSoundUrl);
      sendAskAudio.preload = 'auto';
    }
    sendAskAudio.pause();
    sendAskAudio.currentTime = 0;
    await applyAudioOutput(sendAskAudio);

    void sendAskAudio.play().catch(() => {
      // Ignore autoplay or device audio restrictions.
    });
  } catch {
    // Sound is a best-effort cue only.
  }
}

export async function playEndSound(): Promise<void> {
  if (typeof Audio === 'undefined') return;
  if (!store.getState().drivingMode.drivingCueSoundsEnabled) return;
  try {
    if (!endAudio) {
      endAudio = new Audio(endSoundUrl);
      endAudio.preload = 'auto';
    }
    endAudio.pause();
    endAudio.currentTime = 0;
    await applyAudioOutput(endAudio);
    void endAudio.play().catch(() => {
      // Ignore autoplay or device audio restrictions.
    });
  } catch {
    // Sound is a best-effort cue only.
  }
}

export function suppressLatestAssistantTts(): void {
  const messages = store.getState().messages;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message.role !== 'assistant') continue;
    suppressedTtsMsgIds.add(message.id);
    lastSpokenMsgId = message.id;
    return;
  }
}

/** Convert markdown table blocks to natural spoken text, read line by line */
export function tableToSpokenText(text: string): string {
  const lines = text.split('\n');
  const result: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i].trim();

    // Start of a markdown table (line starts and ends with |)
    if (line.startsWith('|') && line.endsWith('|')) {
      const tableLines: string[] = [lines[i]];
      i++;
      // Collect all consecutive table lines
      while (i < lines.length) {
        const next = lines[i].trim();
        if (next.startsWith('|') && next.endsWith('|')) {
          tableLines.push(lines[i]);
          i++;
        } else {
          break;
        }
      }

      // Parse rows - strip leading/trailing pipes, split on |
      const rows = tableLines.map(l =>
        l.trim().slice(1, -1).split('|').map(c => c.trim())
      );

      // Locate separator row (cells are only dashes/colons)
      const sepIdx = rows.findIndex(r => r.every(c => /^[-:]+$/.test(c)));

      let headers: string[] = [];
      let dataRows: string[][] = [];

      if (sepIdx === 1) {
        headers = rows[0];
        dataRows = rows.slice(2);
      } else if (sepIdx > 1) {
        headers = rows[0];
        dataRows = rows.filter((_, idx) => idx !== 0 && idx !== sepIdx);
      } else {
        dataRows = rows; // no header separator found
      }

      const spoken: string[] = [];

      // Read each data row using column headers as connection words
      dataRows.forEach((row) => {
        if (row.every(c => c === '')) return;
        const parts = headers.length > 0
          ? row.map((cell, colIdx) => `${headers[colIdx] ?? ''} ${cell}`.trim())
          : row;
        spoken.push(parts.join(', ') + '.');
      });

      result.push(spoken.join('\n'));
    } else {
      result.push(lines[i]);
      i++;
    }
  }

  return result.join('\n');
}

/** Strip markdown for clean driving-mode display */
export function cleanForDrivingDisplay(text: string): string {
  return tableToSpokenText(text)
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^#{1,6}\s*/gm, '')
    .replace(/\*{1,3}([^*]+)\*{1,3}/g, '$1')
    .replace(/_{1,3}([^_]+)_{1,3}/g, '$1')
    .replace(/~~([^~]+)~~/g, '$1')
    .replace(/^>\s*/gm, '')
    .replace(/^[\t ]*[-*+]\s+/gm, '- ')
    .replace(/\|/g, ' ')
    .replace(/^[-:|\s]+$/gm, '')
    .replace(/<[^>]+>/g, '')
    .replace(/-{3,}/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

// ---------------------------------------------------------------------------
// Lifecycle: cleanup (exported so McpChatWidget can call it externally)
// ---------------------------------------------------------------------------
export function cleanupDrivingMode(): void {
  clearSilenceTimer();
  clearNoSpeechTimer();
  accumulatedFinal = '';
  lastInterimText = '';
  lastSpokenMsgId = null;
  activeStreamingMsgId = null;
  streamedResponseText = '';
  failedTtsMsgIds.clear();
  suppressedTtsMsgIds.clear();
  recentlySent.length = 0;
  manualStopRequested = false;
  sendAfterStopRequested = false;
  getSttService().abort();
  getTtsService().stop();
}
export function resetSilenceTimer(
  speechDetectedRef: MutableRefObject<boolean>,
  transcriptRef: React.RefObject<HTMLElement | null>,
): void {
  clearSilenceTimer();
  setSilenceTimer(setTimeout(() => {
    if (store.getState().drivingMode.listening && getBestTranscript()) {
      void sendTranscript(speechDetectedRef, transcriptRef);
    }
  }, SILENCE_TIMEOUT_MS));
}
export function clearTranscriptUi(transcriptRef: React.RefObject<HTMLElement | null>): void {
  if (transcriptRef.current) transcriptRef.current.textContent = '';
}

export function resetSpeechDetection(speechDetectedRef: MutableRefObject<boolean>): void {
  if (speechDetectedRef.current !== undefined) {
    speechDetectedRef.current = false;
  }
}

export async function sendTranscript(
  speechDetectedRef: MutableRefObject<boolean>,
  transcriptRef: React.RefObject<HTMLElement | null>,
): Promise<void> {
  clearSilenceTimer();
  clearNoSpeechTimer();
  setManualStopRequested(false);
  setSendAfterStopRequested(false);
  resetSpeechDetection(speechDetectedRef);
  const text = getBestTranscript();
  setAccumulatedFinal('');
  setLastInterimText('');
  clearTranscriptUi(transcriptRef);

  if (!text) {
    store.setDrivingMode({ listening: false, transcript: '' });
    return;
  }

  // Drop transcripts that are identical to something sent in the last 20 s.
  // Whisper can hallucinate the most-recently-spoken phrase when fed background noise.
  if (isDuplicateTranscript(text)) {
    console.debug(`[driving-mode] Duplicate transcript suppressed: "${text}"`);
    store.setDrivingMode({ listening: false, transcript: '' });
    return;
  }

  // Fast-path: obvious patterns (no API call needed)
  if (isStopCommand(text)) {
    triggerStop();
    return;
  }

  // AI classification: ask the LLM whether this means "stop the session".
  // Show processing spinner while we wait (~200–400 ms).
  getSttService().abort();
  store.setDrivingMode({ listening: false, processing: true, transcript: '' });

  const { isStop } = await webchatApi.classifyIntent(text);
  if (isStop) {
    store.setDrivingMode({ processing: false });
    triggerStop();
    return;
  }

  // Normal send
  playSendAskSound();
  recordSentTranscript(text);
  const processed = normalizeSpokenDigitSequences(text);
  console.debug(`[driving-mode] Sending: "${processed}" (raw: "${text}")`);
  const orchestrator = getOrchestratorRef() ?? await ensureOrchestratorRef();
  if (orchestrator) {
    orchestrator.sendMessage(processed, [], true);
  } else {
    console.warn('[driving-mode] No orchestrator available');
    store.setDrivingMode({ processing: false, error: 'sttNetworkError' });
  }
}
export function stopListeningWithoutSending(
  speechDetectedRef: MutableRefObject<boolean>,
  transcriptRef: React.RefObject<HTMLElement | null>,
  playEndTone = false,
): void {
  clearSilenceTimer();
  clearNoSpeechTimer();
  setManualStopRequested(false);
  setSendAfterStopRequested(false);
  resetSpeechDetection(speechDetectedRef);
  setAccumulatedFinal('');
  setLastInterimText('');
  clearTranscriptUi(transcriptRef);
  getSttService().abort();
  store.setDrivingMode({ listening: false, transcript: '', processing: false });
  if (playEndTone) {
    playEndSound();
  }
}

function triggerStop(): void {
  setManualStopRequested(true);
  getSttService().abort();
  suppressLatestAssistantTts();
  getOrchestratorRef()?.interruptCurrentTurn();
  getTtsService().stop();
  setActiveStreamingMsgId(null);
  setStreamedResponseText('');
  store.setDrivingMode({ listening: false, speaking: false, processing: false, transcript: '', error: null });
}
export function hasSendableSpeech(speechDetectedRef: MutableRefObject<boolean>): boolean {
  return !!getBestTranscript() || speechDetectedRef.current;
}
export function startListening(
  speechDetectedRef: MutableRefObject<boolean>,
  transcriptRef: React.RefObject<HTMLElement | null>,
  forceUpdate: React.Dispatch<React.SetStateAction<number>>,
): void {
  setManualStopRequested(false);
  setSendAfterStopRequested(false);
  resetSpeechDetection(speechDetectedRef);
  setAccumulatedFinal('');
  setLastInterimText('');
  clearSilenceTimer();
  clearNoSpeechTimer();
  clearTranscriptUi(transcriptRef);
  store.setDrivingMode({ error: null, transcript: '', listening: true });
  setNoSpeechTimer(setTimeout(() => {
    if (!store.getState().drivingMode.listening || getBestTranscript()) return;
    stopListeningWithoutSending(speechDetectedRef, transcriptRef, true);
  }, NO_SPEECH_TIMEOUT_MS));

  getSttService().start(resolveWidgetLocale(), {
    getContext: () => accumulatedFinal,
    onInterim: (text) => {
      setLastInterimText(text);
      const display = accumulatedFinal ? accumulatedFinal + ' ' + text : text;
      // Direct DOM update - no store re-render - prevents flickering
      if (transcriptRef.current) transcriptRef.current.textContent = display;
      resetSilenceTimer(speechDetectedRef, transcriptRef);
    },
    onFinal: (text) => {
      if (!text.trim()) return;
      appendAccumulatedFinal(text.trim());
      setLastInterimText('');
      if (transcriptRef.current) transcriptRef.current.textContent = accumulatedFinal;
      if (sendAfterStopRequested) {
        void sendTranscript(speechDetectedRef, transcriptRef);
        return;
      }
      resetSilenceTimer(speechDetectedRef, transcriptRef);
    },
    onSpeechDetected: () => {
      speechDetectedRef.current = true;
      clearNoSpeechTimer();
      forceUpdate(c => c + 1);
    },
    onGenderDetected: (gender) => {
      // Only update once per session - gender doesn't change between turns
      if (!store.getState().drivingMode.userGender) {
        store.setDrivingMode({ userGender: gender });
      }
    },
    onNoise: () => {
      // Whisper repeatedly got audio but found no real words - persistent noise.
      // After enough consecutive noise rounds, end the session rather than looping forever.
      stopListeningWithoutSending(speechDetectedRef, transcriptRef, true);
    },
    onError: (errorKey) => {
      clearSilenceTimer();
      clearNoSpeechTimer();
      setSendAfterStopRequested(false);
      if (hasSendableSpeech(speechDetectedRef)) {
        void sendTranscript(speechDetectedRef, transcriptRef);
      } else {
        resetSpeechDetection(speechDetectedRef);
        clearTranscriptUi(transcriptRef);
        store.setDrivingMode({ listening: false, error: errorKey });
      }
    },
    onEnd: () => {
      if (sendAfterStopRequested) {
        const text = getBestTranscript();
        setSendAfterStopRequested(false);
        if (text) {
          void sendTranscript(speechDetectedRef, transcriptRef);
        } else {
          resetSpeechDetection(speechDetectedRef);
          clearTranscriptUi(transcriptRef);
          store.setDrivingMode({ listening: false, processing: false, transcript: '' });
        }
        return;
      }

      // With continuous=true the browser only fires onEnd when recognition truly
      // stops (e.g. permission revoked, network error). Restart immediately.
      if (store.getState().drivingMode.active && store.getState().drivingMode.listening) {
        startListening(speechDetectedRef, transcriptRef, forceUpdate);
      }
    },
  }, { continuous: true });
}
